import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clearAuthToken } from '@noor/api';
import { sessionApi } from '@/entities/session';

/**
 * Sign-out mutation — `POST /auth/logout`, then a full local teardown.
 *
 * The local teardown runs in `onSettled`, not `onSuccess`: a failed revoke must
 * still end the session on this device. Anything else strands the merchant in a
 * signed-in shell whose every request 401s — worse than a token the backend
 * hasn't heard about yet. The network call is best-effort; the local clear is
 * the part that must not fail.
 *
 * The query cache is cleared (not merely invalidated) so the next account can't
 * flash the previous merchant's orders/menu while its own queries are in
 * flight — `clear()` drops the data instead of refetching it.
 */
export function useSignOut(onSuccess: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sessionApi.logout(),
    onSettled: () => {
      clearAuthToken();
      queryClient.clear();
      onSuccess();
    },
  });
}
